import { ColorHoverVariants, colorHoverVariants, shouldForwardPropExclude } from '@synergy/utils';
import { MouseEvent, PropsWithChildren, ReactElement, ReactNode } from 'react';
import { PaletteColor, StackProps, styled, useTheme } from '@mui/material';
import { alpha, getPath } from '@mui/system';

import { PoolRow } from '../../pool-row/pool-row';

export const StyledStack = styled(PoolRow, {
    shouldForwardProp: shouldForwardPropExclude('color', 'disabled', 'hoverColor'),
})<{
    color: string;
    disabled: boolean;
    hoverColor?: string;
}>(({ theme, color, disabled, hoverColor }) => ({
    ...theme.typography.caption,
    marginLeft: 0,
    gap: theme.spacing(1),
    flexWrap: 'nowrap',
    cursor: 'pointer',
    color: color,

    '&:hover': {
        color: hoverColor,
    },
    '&:active': {
        color: theme.palette.grey['800'],
    },

    ...(disabled && {
        color: alpha(color, theme.palette.action.disabledOpacity),
        cursor: 'default',
        '&:hover': undefined,
        '&:active': undefined,
    }),
}));

export interface PoolTextButtonProps extends PropsWithChildren, Pick<StackProps, 'sx' | 'color' | 'className'> {
    startIcon?: ReactNode;
    hoverColor?: string;
    disabled?: boolean;
    onClick?: (e: MouseEvent<HTMLDivElement>) => void;
}

export const PoolTextButton = (props: PoolTextButtonProps): ReactElement => {
    const {
        color = 'grey700',
        hoverColor = 'black',
        disabled = false,
        startIcon,
        children,
        onClick,
        ...stackProps
    } = props;
    const theme = useTheme();

    const palette = (getPath(theme, `palette.${color}`, false) || color) as PaletteColor | string;
    const paletteColor = typeof palette === 'string' ? palette : palette.main;

    const hoverPalette = (getPath(theme, `palette.${hoverColor}`, false) || hoverColor) as PaletteColor | string;
    const hoverPaletteColor = hoverPalette
        ? typeof hoverPalette === 'string'
            ? hoverPalette
            : hoverPalette.main
        : paletteColor;

    const handleClick = (e: MouseEvent<HTMLDivElement>): void => {
        if (!disabled) {
            onClick?.(e);
        }
    };

    return (
        <StyledStack
            onClick={handleClick}
            color={colorHoverVariants(theme)?.[paletteColor as ColorHoverVariants]?.textColor ?? paletteColor}
            hoverColor={
                colorHoverVariants(theme)[paletteColor as ColorHoverVariants]?.hoverColor ??
                hoverPaletteColor ??
                theme.palette.grey[700]
            }
            disabled={disabled}
            {...stackProps}
        >
            {startIcon}
            {children}
        </StyledStack>
    );
};
